import React, { useEffect, useMemo, useState } from "react";
import { X, Trophy, Timer, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fetchTrivia, submitTrivia, submitSuperfanEmail } from "../../lib/trivia";

const LS_DISMISSED_KEY = "cbb-trivia-dismissed-until";

function useCountdown(untilIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!untilIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [untilIso]);
  if (!untilIso) return null;
  const target = new Date(untilIso).getTime();
  const diff = Math.max(0, target - now);
  const mm = String(Math.floor(diff / 60000)).padStart(2, "0");
  const ss = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
  return { diff, mm, ss, expired: diff <= 0 };
}

/**
 * Auto-opens for any signed-in keeper the server has flagged as eligible
 * (personal unlocks >= 60 and not already passed). The server enforces
 * eligibility + cooldown so this component is a UI courier only. Safe to
 * mount globally in the Layout — a single GET fires once per mount.
 */
export default function ArchiveTriviaModal() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // server payload
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({}); // qid -> choice_index
  const [screen, setScreen] = useState("intro"); // intro | quiz | cooldown | win | winRunnerUp | done | already
  const [winEmail, setWinEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [isFirstWinner, setIsFirstWinner] = useState(false);

  const cooldown = useCountdown(state?.cooldown_until);

  // On mount: fetch eligibility once. The server tells us whether to open.
  useEffect(() => {
    // Only run when a bearer token exists — anonymous keepers can't hit this endpoint.
    let hasSession = false;
    try {
      hasSession = !!localStorage.getItem("cbb-session-bearer");
    } catch { /* no-op */ }
    if (!hasSession) return undefined;
    // Respect a fresh dismissal snooze (24h) so a keeper who bails isn't badgered.
    let dismissedUntil = 0;
    try {
      dismissedUntil = parseInt(localStorage.getItem(LS_DISMISSED_KEY) || "0", 10);
    } catch { /* no-op */ }
    if (dismissedUntil && dismissedUntil > Date.now()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchTrivia();
        if (cancelled) return;
        if (!d.eligible) return; // <60 unlocks — quietly stay hidden
        setState(d);
        setOpen(true);
        if (d.already_passed) {
          if (d.email_captured) {
            setScreen("done");
            setIsFirstWinner(!!d.is_first_winner);
          } else {
            setScreen(d.is_first_winner ? "win" : "winRunnerUp");
          }
        } else if (d.cooldown_until) {
          setScreen("cooldown");
        } else if (d.questions?.length) {
          setScreen("intro");
        }
      } catch { /* silent — anonymous or transient error */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    // Snooze for 24h so a keeper who bails isn't badgered mid-scroll.
    try {
      localStorage.setItem(LS_DISMISSED_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* no-op */ }
    setOpen(false);
    setAnswers({});
  };

  const startQuiz = () => setScreen("quiz");

  const canSubmit = useMemo(() => {
    if (!state?.questions) return false;
    return state.questions.every((q) => typeof answers[q.qid] === "number");
  }, [state, answers]);

  const submit = async () => {
    if (!state?.attempt_id || !canSubmit) return;
    setLoading(true);
    try {
      const payload = state.questions.map((q) => ({
        qid: q.qid,
        choice_index: answers[q.qid],
      }));
      const res = await submitTrivia(state.attempt_id, payload);
      if (res.passed) {
        // Reload state to fetch is_first_winner + booth
        toast.success("The archive is yours.");
        setState((s) => ({ ...s, cooldown_until: null }));
        // We don't yet know first-winner status until email step. Optimistically send email screen.
        setScreen("win");
        setAnswers({});
      } else {
        setState((s) => ({ ...s, cooldown_until: res.cooldown_until }));
        setScreen("cooldown");
        setAnswers({});
        toast.error(`${res.correct_count}/${res.total} correct. 30-minute cooldown before another attempt.`);
      }
    } catch (e) {
      toast.error("Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!winEmail || !state?.attempt_id) return;
    setEmailBusy(true);
    try {
      const res = await submitSuperfanEmail(state.attempt_id, winEmail.trim());
      setIsFirstWinner(!!res.is_first_winner);
      setScreen(res.is_first_winner ? "done" : "winRunnerUp");
      toast.success(res.is_first_winner ? "Prize claimed. Booth 2527 awaits." : "Trivia Legend title locked in.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save your email.";
      toast.error(msg);
    } finally {
      setEmailBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-sm animate-[fadeIn_0.4s_ease-out]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trivia-title"
      data-testid="archive-trivia-modal"
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0806] border border-[#C5A059]/30 shadow-[0_0_60px_-15px_rgba(197,160,89,0.35)]">
        <button
          type="button"
          onClick={dismiss}
          data-testid="trivia-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#C5A059]/60 text-[#A39E99] hover:text-[#EAE0D5] transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-6 sm:p-10">
          {loading && !state && (
            <p className="text-[#A39E99] text-sm">Loading the gauntlet…</p>
          )}

          {/* Intro */}
          {screen === "intro" && state?.questions && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">Super Fan Gauntlet</div>
              <h2 id="trivia-title" className="font-display text-3xl sm:text-4xl text-[#EAE0D5] mt-3 leading-[1.1]">
                Five questions. One archive. <span className="text-[#C5A059] italic font-light">Every answer must be true.</span>
              </h2>
              <p className="text-[#A39E99] text-sm mt-4 leading-relaxed">
                You&rsquo;ve recovered every record. Prove your knowledge of the world beneath the veil. A single wrong answer and the scrolls seal for 30 minutes.
              </p>
              <p className="text-[#737373] text-xs mt-3">
                First keeper to answer all five correctly wins a Visa gift card, a permanent Super Fan title, and a lifelong merch connection.
              </p>
              <button
                type="button"
                onClick={startQuiz}
                data-testid="trivia-begin"
                className="mt-8 inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3.5 text-xs tracking-[0.28em] uppercase font-semibold hover:bg-[#D4AF68] transition-colors"
              >
                <Sparkles size={14} /> Begin
              </button>
            </>
          )}

          {/* Quiz */}
          {screen === "quiz" && state?.questions && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">Super Fan Gauntlet</div>
              <h2 id="trivia-title" className="font-display text-2xl sm:text-3xl text-[#EAE0D5] mt-3">Five questions.</h2>
              <div className="mt-6 space-y-6" data-testid="trivia-questions">
                {state.questions.map((q, qi) => (
                  <div key={q.qid} className="border border-white/5 bg-[#050403] p-5">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059]/70">
                      Question {qi + 1}
                    </div>
                    <p className="font-display text-lg text-[#EAE0D5] mt-2">{q.prompt}</p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
                      {q.choices.map((c, ci) => {
                        const picked = answers[q.qid] === ci;
                        return (
                          <button
                            key={ci}
                            type="button"
                            role="radio"
                            aria-checked={picked}
                            onClick={() => setAnswers((a) => ({ ...a, [q.qid]: ci }))}
                            data-testid={`trivia-choice-${q.qid}-${ci}`}
                            className={`text-left text-sm py-3 px-4 border transition-colors ${
                              picked
                                ? "border-[#C5A059] bg-[#C5A059]/15 text-[#EAE0D5]"
                                : "border-white/10 text-[#A39E99] hover:border-[#C5A059]/40 hover:text-[#EAE0D5]"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || loading}
                  data-testid="trivia-submit"
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3 text-xs tracking-[0.28em] uppercase font-semibold hover:bg-[#D4AF68] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Submit
                </button>
                <span className="text-xs text-[#737373]">
                  {Object.keys(answers).length}/{state.questions.length} answered
                </span>
              </div>
            </>
          )}

          {/* Cooldown */}
          {screen === "cooldown" && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#B85C5C]">Sealed</div>
              <h2 id="trivia-title" className="font-display text-3xl text-[#EAE0D5] mt-3">
                The scrolls close for <span className="text-[#B85C5C]">30 minutes</span>.
              </h2>
              <p className="text-[#A39E99] text-sm mt-3">
                A wrong answer costs you time. Return when the veil lifts.
              </p>
              <div
                data-testid="trivia-cooldown"
                className="mt-6 inline-flex items-center gap-2 border border-[#B85C5C]/30 bg-[#1a0a0a] px-5 py-3 text-[#B85C5C] tabular-nums"
              >
                <Timer size={14} /> {cooldown?.mm || "30"}:{cooldown?.ss || "00"}
              </div>
              {cooldown?.expired && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const d = await fetchTrivia();
                      setState(d);
                      if (d.questions?.length) setScreen("intro");
                    } catch { /* silent */ }
                  }}
                  className="ml-3 inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-4 py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#C5A059]/10"
                >
                  Try again
                </button>
              )}
            </>
          )}

          {/* Win — needs email */}
          {screen === "win" && (
            <>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">
                <Trophy size={12} /> Super Fan
              </div>
              <h2 id="trivia-title" className="font-display text-3xl sm:text-4xl text-[#EAE0D5] mt-3 leading-[1.1]">
                The archive is yours.
              </h2>
              <p className="text-[#A39E99] text-sm mt-4 leading-relaxed">
                Enter your email to lock in your prize. You&rsquo;ll receive a Visa gift card by email within 30 business days,
                the permanent <span className="text-[#C5A059]">Super Fan</span> title, and a lifelong merch connection.
              </p>
              <p className="text-[#C5A059] text-sm mt-4">Return to <span className="font-semibold">Booth 2527</span> for your photo op.</p>
              <form onSubmit={submitEmail} className="mt-6 flex gap-2" data-testid="trivia-email-form">
                <input
                  type="email"
                  required
                  value={winEmail}
                  onChange={(e) => setWinEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="trivia-email-input"
                  className="flex-1 bg-[#050403] border border-white/10 focus:border-[#C5A059] px-4 py-3 text-[#EAE0D5] outline-none"
                />
                <button
                  type="submit"
                  disabled={emailBusy}
                  data-testid="trivia-email-submit"
                  className="bg-[#C5A059] text-black px-6 py-3 text-xs tracking-[0.28em] uppercase font-semibold hover:bg-[#D4AF68] disabled:opacity-40"
                >
                  Claim
                </button>
              </form>
            </>
          )}

          {/* Runner-up */}
          {screen === "winRunnerUp" && (
            <>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">Trivia Legend</div>
              <h2 id="trivia-title" className="font-display text-3xl text-[#EAE0D5] mt-3">
                You passed. Another keeper claimed the Visa card first.
              </h2>
              <p className="text-[#A39E99] text-sm mt-4 leading-relaxed">
                You&rsquo;re still crowned a <span className="text-[#C5A059]">Trivia Legend</span> — the highest tier of keeper. Come find us at <span className="font-semibold text-[#EAE0D5]">Booth 2527</span> for a photo op.
              </p>
              <button
                type="button"
                onClick={dismiss}
                data-testid="trivia-runnerup-close"
                className="mt-8 inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-6 py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#C5A059]/10"
              >
                Close
              </button>
            </>
          )}

          {/* Done — first winner locked in */}
          {screen === "done" && (
            <>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">
                <Trophy size={12} /> {isFirstWinner ? "First Super Fan" : "Trivia Legend"}
              </div>
              <h2 id="trivia-title" className="font-display text-3xl text-[#EAE0D5] mt-3">
                {isFirstWinner ? "Prize claimed." : "Legend confirmed."}
              </h2>
              <p className="text-[#A39E99] text-sm mt-4 leading-relaxed">
                {isFirstWinner
                  ? "Your Visa gift card is inbound within 30 business days. Your Super Fan title is permanent. Return to "
                  : "You&rsquo;re a Trivia Legend for life. Return to "}
                <span className="text-[#C5A059] font-semibold">Booth 2527</span> for your photo op.
              </p>
              <button
                type="button"
                onClick={dismiss}
                data-testid="trivia-done-close"
                className="mt-8 inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-6 py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#C5A059]/10"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
